import { ApolloQueryResult } from 'apollo-client';
import deepmerge from 'deepmerge';
import { chunk } from 'lodash-es';

import { IEvaluateUserOffersQuery, IEvaluateUserOffersQueryVariables } from 'generated/rbi-graphql';
import { ServiceMode } from 'state/service-mode/types';
import { RBIPlatform } from 'utils/environment';

const MAX_COUPONS_PER_REQUEST = 10;

/**
 * Turn an array of coupon ids into variables for one or more GraphQL queries for EvaluateUserOffers
 */
export const couponIdsToQueryParams = ({
  couponIds,
  evaluateAllCouponIds,
  platform,
  serviceMode,
  redeemedOn,
  storeId,
}: {
  couponIds: readonly string[];
  evaluateAllCouponIds: boolean;
  platform: RBIPlatform;
  serviceMode: ServiceMode | null;
  redeemedOn: string;
  storeId: string | null;
}): IEvaluateUserOffersQueryVariables[] => {
  // if no coupon ids were passed in, return one queryParams anyway so that EvaluateUserOffers can be called
  if (!couponIds.length) {
    return [
      {
        couponIds: [],
        evaluateAllCouponIds,
        redeemedOn,
        platform,
        serviceMode,
        storeId,
      },
    ];
  }

  return chunk(couponIds, MAX_COUPONS_PER_REQUEST).map(chunkOfCouponIds => ({
    couponIds: chunkOfCouponIds,
    evaluateAllCouponIds,
    redeemedOn,
    platform,
    serviceMode,
    storeId,
  }));
};

/**
 * Reduce the latest response from using fetchMore with the previous responses
 * @param prev existing responses from EvaluateUserOffers
 * @param an object containing another response from EvaluateUserOffers to combine
 */
export const combineWithPreviousResponse = (
  prev: IEvaluateUserOffersQuery,
  { fetchMoreResult }: { fetchMoreResult: IEvaluateUserOffersQuery }
) => {
  if (!fetchMoreResult && prev) {
    return prev;
  }

  return deepmerge(prev || {}, fetchMoreResult);
};

/* this function generated by Apollo, and I'm re-creating a more specfic version of its interface here */
interface IFetchMoreArgs {
  ({
    variables,
    updateQuery,
  }: {
    variables: IEvaluateUserOffersQueryVariables;
    updateQuery: (
      prev: IEvaluateUserOffersQuery,
      { fetchMoreResult }: { fetchMoreResult: IEvaluateUserOffersQuery }
    ) => IEvaluateUserOffersQuery;
  }): Promise<ApolloQueryResult<IEvaluateUserOffersQuery>>;
}

/**
 * Iterate from the 2nd to the last queries prepared in queryParamsList, making calls to
 * @param fetchMore - the function returned by an Apollo useQuery to make more queries to the same endpoint
 * @param queryParamsList - variables for one ore more queries, probably prepared by `couponIdsToQueryParams`
 */
export function makeRemainingEvaluateUserOfferQueries(
  fetchMore: IFetchMoreArgs,
  queryParamsList: IEvaluateUserOffersQueryVariables[],
  isAuthenticated: boolean
) {
  if (!isAuthenticated) {
    return Promise.resolve();
  }
  return Promise.all(
    queryParamsList.map(queryParams => {
      return fetchMore({
        variables: queryParams,
        updateQuery: combineWithPreviousResponse,
      });
    })
  );
}
